import { AlertCircle, CheckCircle2, LoaderCircle, X } from "lucide-react";
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { forwardRef, useEffect, useId } from "react";
import "./mobile-modal.css";

export function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger";
  size?: "small" | "medium";
  busy?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = "primary", size = "medium", busy, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={props.type ?? "button"}
      className={cx("button", `button--${variant}`, `button--${size}`, className)}
      disabled={disabled || busy}
      {...props}
    >
      {busy ? <LoaderCircle className="spin" aria-hidden="true" size={17} /> : null}
      {children}
    </button>
  );
});

export function IconButton({
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={props.type ?? "button"} className={cx("icon-button", className)} {...props}>
      {children}
    </button>
  );
}

type FieldProps = {
  label: string;
  hint?: string;
  error?: string;
  className?: string;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
};

export function Field({ label, hint, error, className, children }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cx("field", className)}>
      <label htmlFor={id}>{label}</label>
      {children({ id, describedBy })}
      {error ? (
        <p className="field__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input(props, ref) {
    return <input ref={ref} className={cx("input", props.className)} {...props} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select(props, ref) {
    return <select ref={ref} className={cx("input", props.className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea(props, ref) {
  return <textarea ref={ref} className={cx("input textarea", props.className)} {...props} />;
});

export function Badge({
  tone = "neutral",
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: string }) {
  return <span className={cx("badge", `badge--${tone}`, className)} {...props} />;
}

export function Spinner({ label = "Caricamento in corso" }: { label?: string }) {
  return (
    <output className="spinner">
      <LoaderCircle className="spin" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </output>
  );
}

export function Alert({
  tone = "error",
  title,
  children,
  action,
}: {
  tone?: "error" | "success" | "warning" | "info";
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const Icon = tone === "success" ? CheckCircle2 : AlertCircle;
  return (
    <div className={cx("alert", `alert--${tone}`)} role={tone === "error" ? "alert" : "status"}>
      <Icon aria-hidden="true" size={19} />
      <div className="alert__content">
        <strong>{title}</strong>
        {children ? <div>{children}</div> : null}
      </div>
      {action ? <div className="alert__action">{action}</div> : null}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: { icon?: ReactNode; title: string; description: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      {icon ? <div className="empty-state__icon">{icon}</div> : null}
      <h2>{title}</h2>
      <p>{description}</p>
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}

export function LoadingBlock({
  lines = 4,
  label = "Caricamento dei dati",
}: { lines?: number; label?: string }) {
  const skeletonKeys = [
    "first",
    "second",
    "third",
    "fourth",
    "fifth",
    "sixth",
    "seventh",
    "eighth",
  ];
  return (
    <div className="loading-block" aria-label={label} aria-busy="true">
      {skeletonKeys.slice(0, lines).map((key, index) => (
        <div className={`skeleton skeleton--line-${index + 1}`} key={key} />
      ))}
    </div>
  );
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = "default",
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** "wide" for reading a lot side by side, like the analysis of a proposal. */
  size?: "default" | "wide";
}) {
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Taken: the panel below (the chat in Approvazioni) stays open.
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <dialog
        open
        className={cx("modal", size === "wide" && "modal--wide")}
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby={description ? "modal-description" : undefined}
      >
        <header className="modal__header">
          <div>
            <h2 id="modal-title">{title}</h2>
            {description ? <p id="modal-description">{description}</p> : null}
          </div>
          <IconButton type="button" aria-label="Chiudi finestra" onClick={onClose}>
            <X aria-hidden="true" size={19} />
          </IconButton>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </dialog>
    </div>
  );
}
